"use client"

import React, { useEffect, useRef } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { fetchClientsByUserId, setSelectedClient } from "../../store/slices/clientsSlice"
import { getClientWorldCityName } from "../../lib/worldCity"
import { useInboxNotifications } from "../../contexts/InboxNotificationContext"
import "./Header.css"
import { fetchOrganizations } from "../../store/slices/settingOrganizationSlice"
import { fetchCollaborators } from "../../store/slices/collaboratorsSlice"
import Tooltip from '@mui/material/Tooltip';

export const Header = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const inboxCtx = useInboxNotifications()
  const isInboxRoute = location.pathname.includes('ai-community-inbox')
  const notifications = inboxCtx?.notifications ?? []
  const rawUnread = (notifications ?? []).filter((n) => n.read !== true).length
  const unreadCount = Math.max(0, rawUnread)
  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount)
  const drawerOpen = inboxCtx?.drawerOpen ?? false
  const setDrawerOpen = inboxCtx?.setDrawerOpen ?? (() => {})
  const isSettingsPage = location.pathname === '/settings'
  const isAicommunityInbox = location.pathname === '/ai-community-inbox'
  const isMultiPropertyDashboard =
    location.pathname.replace(/\/+$/, "") === "/multi-property-dashboard"

  const { profile: user } = useSelector((state) => state.user)
  const { clients, selectedClient, status: clientsStatus } = useSelector((state) => state.clients)
  const { organizations } = useSelector((state) => state.organizations || {})
  const orgLogo = organizations?.[0]?.logo || null
  const orgName = organizations?.[0]?.name || ""
  const orgInitial = orgName ? orgName.charAt(0).toUpperCase() : ""
  const [orgLogoError, setOrgLogoError] = React.useState(false)

  React.useEffect(() => {
    setOrgLogoError(false)
  }, [orgLogo])
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState("")
  const [activeIndex, setActiveIndex] = React.useState(-1)

  const wrapperRef = React.useRef(null)
  const inputRef = React.useRef(null)
  const triggerRef = React.useRef(null)
  const lastFetchedClientRef = useRef(null)
  const propertyTextRef = React.useRef(null)
  const [isTextTruncated, setIsTextTruncated] = React.useState(false)

  const fetchedWhenEmptyRef = useRef(false)
  React.useEffect(() => {
    if (!user?.id) return
    const hasNoClients = !clients?.length
    const shouldFetchIdle = clientsStatus === "idle"
    const shouldFetchEmpty =
      hasNoClients &&
      (clientsStatus === "succeeded" || clientsStatus === "failed") &&
      !fetchedWhenEmptyRef.current
    if (shouldFetchIdle || shouldFetchEmpty) {
      if (shouldFetchEmpty) fetchedWhenEmptyRef.current = true
      dispatch(
        fetchClientsByUserId({
          userId: user.id,
          organizationId: organizations?.[0]?.id,
        }),
      )
    }
  }, [user?.id, clientsStatus, clients?.length, dispatch, organizations?.[0]?.id])

  useEffect(() => {
    if (user?.id) {
      dispatch(fetchOrganizations({ userId: user.id }))
    }
  }, [dispatch, user?.id])

  useEffect(() => {
    if (selectedClient && user?.id) {
      const clientId = selectedClient.id || selectedClient.clientId
      if (clientId && lastFetchedClientRef.current !== clientId) {
        lastFetchedClientRef.current = clientId
        dispatch(fetchCollaborators({ clientId, userId: user.id }))
      }
    }
  }, [selectedClient, user?.id, dispatch])


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target)
      ) {
        setOpen(false)
        setActiveIndex(-1)
        triggerRef.current?.blur()
      }
    }

    document.addEventListener("mousedown", handleClickOutside)

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const clientNames = clients.map((client) => client.name || `Client ${client.id}`)

  const getOptionLocation = (c) => getClientWorldCityName(c) || null

  const clientOptions = React.useMemo(() => {
    return (clients || []).map((c) => {
      const name = c?.name ?? c?.clientName ?? ""
      const location = getOptionLocation(c)
      return {
        id: c.id ?? c.clientId,
        name,
        location,
        label: location ? `${name}, ${location}` : name,
      }
    })
  }, [clients])

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return clientOptions

    return clientOptions.filter((option) =>
      option.label.toLowerCase().includes(q)
    )
  }, [clientOptions, query])

  const handleSelect = (clientId) => {
    const selected = clients.find(
      (c) => (c.id || c.clientId) === clientId
    )

    if (selected) {
      dispatch(setSelectedClient(selected))
      try {
        const n = selected.name || selected.clientName
        if (n) localStorage.setItem("global_selected_property_v1", n)
      } catch (_) {}
      window.dispatchEvent(
        new CustomEvent("soho:property:change", { detail: { value: selected.name || selected.clientName || "all" } }),
      )
    }

    setOpen(false)
    setQuery("")
    setActiveIndex(-1)
    triggerRef.current?.blur()
  }
  const selectedLabel = selectedClient
    ? (() => {
        const city = getClientWorldCityName(selectedClient)
        const name = selectedClient.name || ""
        return city ? `${name}, ${city}` : name || "Select property"
      })()
    : clientsStatus === "loading"
      ? "Loading..."
      : "Select property"
  const selectedClientName = selectedClient?.name ?? ""
  const selectedLocation = getClientWorldCityName(selectedClient)

  const checkTruncation = React.useCallback(() => {
    const el = propertyTextRef.current
    if (!el) return
    setIsTextTruncated(el.scrollWidth > el.clientWidth)
  }, [])

  React.useEffect(() => {
    checkTruncation()
    const el = propertyTextRef.current
    if (!el) return
    const observer = new ResizeObserver(checkTruncation)
    observer.observe(el)
    return () => observer.disconnect()
  }, [selectedClientName, checkTruncation])

  const onKeyDown = (e) => {
    if (!open) return
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setActiveIndex((prev) => Math.min(prev + 1, filtered.length - 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setActiveIndex((prev) => Math.max(prev - 1, 0))
    } else if (e.key === "Enter") {
      e.preventDefault()
      if (activeIndex >= 0 && activeIndex < filtered.length) {
        handleSelect(filtered[activeIndex])
      }
    } else if (e.key === "Escape") {
      e.preventDefault()
      setOpen(false)
      setActiveIndex(-1)
      triggerRef.current?.blur()
    }
  }

  const handleDropdownClick = () => {
    setOpen((wasOpen) => {
      if (wasOpen) triggerRef.current?.blur()
      return !wasOpen
    })
  }

  React.useEffect(() => {
    if (open) {
      setTimeout(() => {
        const dropdown = document.querySelector(".property-popover")
      }, 100)
    }
  }, [open])

  return (
    <header className="header">
      <div className="header-content">
        <div className="logo-section">
          <img
            src="/assets/brand/soho_social.svg"
            className="header-soho-logo"
            alt="SoHo Logo"
          />

          {orgLogo && !orgLogoError ? (
            <img
              src={orgLogo}
              alt="Organization Logo"
              className="logo-image"
              onError={() => setOrgLogoError(true)}
            />
          ) : orgInitial ? (
            <div className="logo-placeholder logo-initial">
              {orgInitial}
            </div>
          ) : null}

          {!isSettingsPage && !isAicommunityInbox && !isMultiPropertyDashboard &&(
            <div className="property-selector" ref={wrapperRef}>
              <span className="property-static-label">Property</span>

              <button
                type="button"
                ref={triggerRef}
                className="property-trigger"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls="property-listbox"
                aria-label="Choose property"
                onClick={handleDropdownClick}
                onKeyDown={onKeyDown}
                disabled={clientsStatus === "loading"}
              >
                <Tooltip title={isTextTruncated ? `${selectedClientName}, ${selectedLocation}` : ""} disableHoverListener={!isTextTruncated}>
                  <span ref={propertyTextRef} className="property-selected">{selectedLabel}</span>
                </Tooltip>
              </button>

              {open && (
                <div className="property-popover" role="dialog">
                  <input
                    ref={inputRef}
                    className="property-search"
                    type="text"
                    placeholder="Search properties..."
                    aria-label="Search properties"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value)
                      setActiveIndex(-1)
                    }}
                    onKeyDown={onKeyDown}
                  />
                  <ul className="property-options">
                    {filtered.length === 0 && (
                      <li className="no-results">No results</li>
                    )}

                    {filtered.map((option, index) => (
                      <li
                        key={option.id}
                        className={`property-option ${index === activeIndex ? "is-active" : ""
                          }`}
                        onMouseDown={(e) => {
                          e.preventDefault()
                          handleSelect(option.id)
                        }}
                        onMouseEnter={() => setActiveIndex(index)}
                      >
                        <div>
                          <div>{option.name}</div>
                          {option.location && (
                            <div style={{ fontSize: "12px", color: "#888" }}>
                              {option.location}
                            </div>
                          )}
                        </div>

                        {(selectedClient?.id || selectedClient?.clientId) === option.id && (
                          <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                            <path
                              d="M7.5 10l2 2 3-4"
                              stroke="#10b981"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        {isInboxRoute && (
          <div className="header-right">
            <button
              type="button"
              className={`header-notification-btn ${drawerOpen ? 'header-notification-btn--active' : ''}`}
              onClick={() => {
                if (!isInboxRoute) {
                  setDrawerOpen(true)
                  navigate('/ai-community-inbox')
                } else {
                  setDrawerOpen((v) => !v)
                }
              }}
              aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
              title="AI response notifications"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              {unreadCount > 0 && (
                <span className="header-notification-badge">{badgeLabel}</span>
              )}
            </button>
          </div>
        )}
      </div>
    </header>
  )
}
